<template>
	<VDataTable
		:items-per-page="-1"
		:headers="headers"
		:items="filteredPayees"
		hide-default-footer
		height="100%"
		fixed-header
		class="agreement-payment-table border rounded bg-surface"
		:style="{ maxHeight: '100%' }"
	>
		<template
			v-for="item,index in payees[0]?.transactions || []"

			:key="`header.transactions${index}amount`"
			#[`header.transactions${index}amount`]="{ column}"
		>
			<div :key="item.due_at">
				<div v-date-time-format="{date: column.title ?? '', pattern:'eee'}" />
				<div
					v-date-time-format="{date: column.title ?? '', pattern:'MM/dd/yy'}"
					class="text-subtitle-1 text-medium-emphasis"
				/>
			</div>
		</template>

		<template #item="{ item }: { item: AgreementPaymentTablePayee }">
			<tr>
				<td
					class="text-no-wrap  text-truncate"
					:class="{ 'v-data-table-column--fixed': !mobile }"
					:style="{ maxWidth: columnWidths.athlete + 'px', width: columnWidths.athlete + 'px' }"
				>
					<div class="d-flex align-center">
						<CommonAvatar
							:src="item.athlete.profileImage"
							:size="32"
							class="flex-0-0"
						/>
						<span class="ml-2 text-high-emphasis font-weight-semibold flex-1-1 text-truncate">
							{{ item.athlete.fullName }}
						</span>
					</div>
				</td>
				<td
					class="bg-neutral-25 "
					:class="{'v-data-table-column--fixed  v-data-table-column--last-fixed': !mobile}"
					:style="{ maxWidth: columnWidths.contractValue + 'px', left: columnWidths.athlete + 'px', width: columnWidths.contractValue + 'px' }"
				>
					<span
						v-formatCentsToDollars="sumOfTransactions(item)"
					/>
				</td>
				<td
					v-for="transaction in item.transactions"
					:key="`${item.athlete.fullName}+${transaction.due_at}`"
				>
					<span
						v-formatCentsToDollars="transaction.amount"
					/>
				</td>
				<td :style="{ width: columnWidths.actions }">
					<VDefaultsProvider
						:defaults="{
							VBtn: {
								class: 'text-medium-emphasis',
								size: 'x-small',
								variant: 'text',
								icon: true,
							},
							VIcon: { size: '20' },
						}"
					>
						<VBtn
							data-testid="buttonPaymentTableEdit"
							:aria-label="t('editAgreement')"
							@click="openExchangeAgreementPayeeDrawer(item)"
						>
							<VIcon>edit</VIcon>
						</VBtn>
					</VDefaultsProvider>
				</td>
			</tr>
		</template>
	</VDataTable>
</template>
<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import { type BaseExchangeAthlete } from '@/types/exchangeAthletes';
import { VDataTable } from 'vuetify/lib/components/VDataTable/index.js';
import { type ApiV1 } from '@teamworksdev/influencer/core';
import vFormatCentsToDollars from '@/directives/formatCentsToDollars';
import { CommonAvatar, type PaymentScheduleExport } from '@teamworksdev/vuetify';
import type { Agreement } from '@/types/agreements';
import { type SingleExchangeAthleteRateCard } from '@/types/exchangeAthletes';
import { useExchangeAgreementPayeeDrawer } from '@/composables/useExchangeAgreementPayeeDrawer';
import { whenever } from '@vueuse/core';
import { getIsoDateString } from '@/utils/dates';
import { evenlyDistributeAmounts } from '@/utils/helpers';
import vDateTimeFormat from '@/directives/dateTimeFormat';


export type AgreementPaymentTablePayee = {
	athlete: BaseExchangeAthlete;
	transactions: Pick<ApiV1.AgreementTransaction, 'amount' | 'due_at' | 'name'>[];
};

const { t } = useI18n();
const { mobile } = useDisplay();

type TableType = InstanceType<typeof VDataTable>;

const payees = defineModel<AgreementPaymentTablePayee[]>('payees', { required: true });
const agreement = defineModel<Agreement | null>('agreement', { required: false });
const rateCard = defineModel<SingleExchangeAthleteRateCard>('rateCard', { required: false });

const props = withDefaults(defineProps<{
	dates?: PaymentScheduleExport;
	athletes?: BaseExchangeAthlete[];
	search?: string | undefined;
}>(), {
	dates: () => [],
	search: undefined,
	athletes: () => [],
});

const columnWidths = {
	athlete: 256,
	contractValue: 160,
	actions: '4rem',
};

const headers = computed(() => {
	const _headers: TableType['$props']['headers'] = [
		{
			title: t('detail', 0),
			align: 'start',
			fixed: !mobile.value,
			headerProps: { class: 'bg-surface' },
			children: [
				{
					title: t('athlete', 1),
					key: 'athlete.fullName',
					fixed: !mobile.value,
					width: columnWidths.athlete,
					minWidth: `${columnWidths.athlete}px`,
				},
				{
					title: t('agreementPaymentTable.contractValue'),
					key: 'total',
					sortable: false,
					fixed: !mobile.value,
					width: columnWidths.contractValue,
				},
			],
		},
	];

	const _headersArray = Array.from(_headers);

	_headersArray.push({
		title: t('agreementWorkflow.payment.title'),
		align: 'start',
		headerProps: { class: 'bg-surface' },
		children: payees.value.length
			? [...payees.value[0]!.transactions.map((transaction, index) => ({
				title: transaction.due_at,
				key: `transactions${index}amount`,
				sortable: false,
			})), { key: 'actions', sortable: false, width: columnWidths.actions }]
			: [{ key: 'actions', sortable: false, width: columnWidths.actions }],
	});
	return _headersArray;
});


const sumOfTransactions = (payee: AgreementPaymentTablePayee) => {
	return payee.transactions.reduce((acc, transaction) => {
		return acc + (transaction.amount ?? 0);
	}, 0);
};


whenever(() => props.dates, (val) => {
	if (val && val.length) {
		populatePaymentScheduleTable();
	}
});


const populatePaymentScheduleTable = () => {
	if (!props.dates || !props.dates.length) return;
	if (rateCard.value) {
		const paymentAmounts = evenlyDistributeAmounts(rateCard.value.cost, props.dates.length);
		const transactions = props.dates.map((date, index) => ({
			amount: paymentAmounts[index],
			name: '',
			due_at: getIsoDateString(date)!,
		}));

		const _payees = props.athletes.map((athlete) => {
			return {
				athlete,
				transactions,
			};
		});
		payees.value = _payees;
		rateCard.value = undefined;
		return;
	}
	if (agreement.value) {
		const _payees = props.athletes.map((athlete) => {
			const transactions = agreement.value!.transactions.map((transaction) => ({
				amount: transaction.cost,
				name: transaction.name,
				due_at: getIsoDateString(transaction.date)!,
			}));
			return {
				athlete,
				transactions,
			};
		});
		payees.value = _payees;
		agreement.value = undefined;
		return;
	}

	const _payees = props.athletes.map((athlete) => {
		const existingPayee = payees.value.find((p) => p.athlete.uuid === athlete.uuid);

		if (existingPayee && existingPayee.transactions.length === props.dates.length) {
			const transactions = existingPayee.transactions.map((transaction, index) => ({
				...transaction,
				due_at: getIsoDateString(props.dates[index])!,
			}));
			return {
				athlete,
				transactions,
			};
		}

		const previousTotal = existingPayee
			? existingPayee.transactions.reduce((sum, tx) => sum + tx.amount, 0)
			: 0;

		const newPaymentAmounts = evenlyDistributeAmounts(previousTotal, props.dates.length);

		const transactions = props.dates.map((date, index) => ({
			amount: newPaymentAmounts[index],
			name: '',
			due_at: getIsoDateString(date)!,
		}));

		return {
			athlete,
			transactions,
		};
	});
	payees.value = _payees;
};


const filteredPayees = computed(() => {
	return props.search
		? payees.value.filter((payee) => {
			return payee.athlete.fullName.toLowerCase().includes(props.search!.toLowerCase());
		})
		: payees.value;
});

// PAYEE DRAWER
const updatePayee = (payee: AgreementPaymentTablePayee) => {
	const index = payees.value.findIndex((p) => p.athlete.uuid === payee.athlete.uuid);
	if (index === -1) return;
	payees.value[index] = payee;
};

const updateAllPayees = (transactions: AgreementPaymentTablePayee['transactions']) => {
	payees.value.forEach((payee) => {
		payee.transactions = [...transactions];
	});
};

const {
	open: openExchangeAgreementPayeeDrawer,
} = useExchangeAgreementPayeeDrawer({
	onUpdated: updatePayee,
	onUpdateAll: updateAllPayees,
});


</script>

<style scoped lang="scss">
@use "sass:map";
@use "@/styles/settings.scss";

$shadowStrength: 0.08;
$gradientStart: rgba(0, 0, 0, $shadowStrength);
$gradientEnd: rgba(0, 0, 0, 0);

.agreement-payment-table :deep(table) {
	> thead {
		z-index: 3;
		white-space: nowrap;
	}


	/* Actions */
	> tbody > tr > td:nth-last-child(1),
	> thead > tr:last-of-type > th:nth-last-child(1) {
		position: sticky !important;
		right: 0;

		&::after {
			content: '';
			position: absolute;
			right: 100%;
			top: 0;
			width: 8px;
			height: 100%;
			opacity: 0.75;
			background: linear-gradient(to left, $gradientStart, $gradientEnd);
		}
	}

	.v-data-table-column--last-fixed {
		border-right: none !important;
	}

	/* Transaction name */
	.v-data-table-column--fixed {
		@media screen and (min-width: map.get(settings.$grid-breakpoints, "md")) {
			&::after {
				content: '';
				position: absolute;
				left: 100%;
				top: 0;
				width: 8px;
				height: 100%;
				opacity: 0.75;
				background: linear-gradient(to right, $gradientStart, $gradientEnd);
			}
		}
	}
}
</style>
